import { useCallback, useState } from 'react';
import { useSWRConfig } from 'swr';
import { toast } from 'sonner';
import { revalidateTransactionCaches, useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { transactionsApi, type RenameBatchPreview } from '@/lib/api';
import { notifyError } from '@/lib/notify-error';
import { track } from '@/lib/telemetry';
import type { Transaction } from '@/types';

/**
 * Rename-everywhere (VER-377): the TransactionDetailDrawer rename contract for hosts
 * OUTSIDE the Transactions screen (home widget, category hub, recurring history).
 * Those hosts have no optimistic page cache to patch — a global revalidation heals
 * their lists, and `onUpdated` hands the returned row back for any locally-held
 * state (a captured `detailTx`, a hand-paged list) that no cache refresh reaches.
 *
 * The Transactions screen keeps its own handler: it additionally patches the
 * infinite pages in place and schedules the heavy revalidation.
 */
export function useTransactionRename({ surface, onUpdated, onBatchApplied }: {
  /** Telemetry + error-surface tag for where the rename happened. */
  surface: 'home' | 'orcamento' | 'recurring_history';
  /** Called with the refreshed source row — after a single rename AND after a batch
   *  (the batch refetches it), so a captured drawer row always heals. */
  onUpdated?: (updated: Transaction) => void;
  /** Batch outcome for hosts holding LISTS outside SWR: the updated ids and the name
   *  they now carry (null = restored) — enough to patch rows without a refetch. */
  onBatchApplied?: (updatedIds: readonly string[], name: string | null) => void;
}) {
  const { mutate: mutateGlobal } = useSWRConfig();
  const authedFetch = useAuthedFetcher();
  const [isRenameSaving, setIsRenameSaving] = useState(false);

  const handleRename = async (txId: string, name: string | null, applyBatch: boolean) => {
    setIsRenameSaving(true);
    try {
      if (applyBatch) {
        const result = await authedFetch(
          (token) => transactionsApi.applyRenameBatch(token, { sourceTransactionId: txId, name }),
        );
        track('action.transaction.rename', {
          source: surface, mode: 'batch_same_description', count: result.updatedCount, cleared: name === null,
        });
        toast.success(result.updatedCount === 1
          ? '1 lançamento renomeado'
          : `${result.updatedCount} lançamentos renomeados`);
        onBatchApplied?.(result.updatedTransactionIds, name);
        if (onUpdated) {
          // The batch returns ids only; the open drawer shows the SOURCE row, so
          // refetch just that one. Best effort — SWR-fed hosts heal via the
          // revalidation below either way.
          try {
            onUpdated(await authedFetch((token) => transactionsApi.getOne(token, txId)));
          } catch {
            // keep the toast outcome; the row heals on the next revalidation
          }
        }
      } else {
        const updated = await authedFetch((token) => transactionsApi.rename(token, txId, name));
        onUpdated?.(updated);
        track('action.transaction.rename', {
          source: surface, mode: 'single', count: 1, cleared: name === null,
        });
        toast.success(name === null ? 'Nome original restaurado' : 'Nome atualizado');
      }
      revalidateTransactionCaches(mutateGlobal);
    } catch (err) {
      notifyError(surface, err, 'Não foi possível renomear o lançamento');
    } finally {
      setIsRenameSaving(false);
    }
  };

  // Memoized: TransactionDetailDrawer keys its one-fetch-on-arm preview effect on this
  // callback's identity — a fresh function per host render would refetch on every
  // rerender while the batch checkbox is checked.
  const handlePreviewRenameBatch = useCallback(
    (txId: string): Promise<RenameBatchPreview> =>
      // The batch population is name-independent — preview with the neutral clear payload.
      authedFetch((token) => transactionsApi.previewRenameBatch(token, { sourceTransactionId: txId, name: null })),
    [authedFetch],
  );

  return { isRenameSaving, handleRename, handlePreviewRenameBatch };
}
